import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Scan, Check, Trash2, Clock, Truck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as validationService from '@/services/validationService';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
/**
 * Slice D4 — Per-section Internal Transfer clone (Salem-untouched).
 *
 * Literal-copy of pages/Sections/GenericInternalTransfer.jsx with the
 * 9 `/internal-transfer/...` URLs swapped to the per-section clone
 * endpoints (`/per-section/{slug}/internal-transfer/...`). The slug is
 * derived from useParams. Salem's GenericInternalTransfer.jsx is NOT
 * imported nor modified.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  return detail || fallback;
};

const PerSectionInternalTransferClone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/internal-transfer`;

  // Staff verification
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');

  // Transfer form
  const [sections, setSections] = useState([]);
  const [targetSection, setTargetSection] = useState('');
  const [isRework, setIsRework] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Outgoing pending shipment confirmations
  const [pendingShipments, setPendingShipments] = useState([]);
  const [confirmingShipment, setConfirmingShipment] = useState(null);

  // Incoming shipped transfers
  const [shippedTransfers, setShippedTransfers] = useState([]);
  const [acceptingTransfer, setAcceptingTransfer] = useState(null);

  // Rejected transfers (reshippable)
  const [rejectedTransfers, setRejectedTransfers] = useState([]);
  const [reshipModal, setReshipModal] = useState({ open: false, transfer: null });
  const [reshipSection, setReshipSection] = useState('');
  const [reshipRemarks, setReshipRemarks] = useState('');
  const [reshipSubmitting, setReshipSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState('outgoing');
  const [loading, setLoading] = useState(false);

  const barcodeRef = useRef(null);

  useEffect(() => {
    fetchSections();
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchSections = async () => {
    try {
      const response = await apiClient.get(`${apiBase}/sections`);
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const shipmentRes = await apiClient.get(`${apiBase}/shipment-requests`);
      setPendingShipments(shipmentRes.data.transfers || []);

      const shippedRes = await apiClient.get(`${apiBase}/shipped-transfers`);
      setShippedTransfers(shippedRes.data.transfers || []);

      const rejectedRes = await apiClient.get(`${apiBase}/rejected-transfers`);
      setRejectedTransfers(rejectedRes.data.transfers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStaffVerify = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter staff ID');
      return;
    }
    try {
      const result = await validationService.validateStaffId(staffId);
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staff?.staff_name || result.staff?.name || 'Staff');
        toast.success(`Welcome, ${result.staff?.staff_name || 'Staff'}`);
        setTimeout(() => barcodeRef.current?.focus(), 100);
      } else {
        toast.error(result.message || 'Invalid staff ID');
      }
    } catch (error) {
      toast.error('Failed to verify staff');
    }
  };

  const handleScanBundle = async () => {
    if (!bundleBarcode.trim()) return;

    if (scannedBundles.includes(bundleBarcode)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      return;
    }

    try {
      const response = await apiClient.post(
        `${apiBase}/validate-bundle?bundle_code=${encodeURIComponent(bundleBarcode)}`
      );
      if (response.data.valid) {
        setScannedBundles([...scannedBundles, bundleBarcode]);
        toast.success(`Bundle ${bundleBarcode} added`);
      } else {
        toast.error(response.data.message || 'Invalid bundle');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to validate bundle'));
    }

    setBundleBarcode('');
    barcodeRef.current?.focus();
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(scannedBundles.filter((b) => b !== barcode));
  };

  const handleSubmitTransfer = async () => {
    if (!targetSection || scannedBundles.length === 0) {
      toast.error('Please select destination and scan bundles');
      return;
    }
    setSubmitting(true);
    try {
      const isReworkSubmit = isRework && (targetSection || '').toLowerCase().includes('inhouse');
      if (isReworkSubmit && !reworkReason.trim()) {
        toast.error('Provide a rework reason');
        setSubmitting(false);
        return;
      }
      await apiClient.post(`${apiBase}/create`, {
        staff_id: staffId,
        staff_name: staffName,
        to_section: targetSection,
        bundle_codes: scannedBundles,
        remarks,
        transfer_type: isReworkSubmit ? 'REWORK' : 'STANDARD',
        rework_reason: isReworkSubmit ? reworkReason.trim() : undefined,
      });
      toast.success(`Transfer created to ${targetSection}.`);
      setScannedBundles([]);
      setTargetSection('');
      setRemarks('');
      setIsRework(false);
      setReworkReason('');
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create transfer'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post(`${apiBase}/confirm-shipment`, { transfer_id: transferId });
      toast.success('Shipment confirmed successfully');
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to confirm shipment'));
    } finally {
      setConfirmingShipment(null);
    }
  };

  const handleAcceptTransfer = async (transferId) => {
    setAcceptingTransfer(transferId);
    try {
      await apiClient.post(`${apiBase}/accept`, { transfer_id: transferId });
      toast.success('Transfer received! Proceed to Bundle Receipt Entry.');
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to accept transfer'));
    } finally {
      setAcceptingTransfer(null);
    }
  };

  const handleOpenReshipModal = (transfer) => {
    setReshipModal({ open: true, transfer });
    setReshipSection('');
    setReshipRemarks('');
    setReshipSubmitting(false); // was left true after a successful reship → next reship silently blocked
  };

  const handleCloseReshipModal = () => {
    setReshipModal({ open: false, transfer: null });
    setReshipSection('');
    setReshipRemarks('');
    setReshipSubmitting(false);
  };

  const handleReshipTransfer = async () => {
    if (!reshipSection || reshipSubmitting) {
      if (!reshipSection) toast.error('Please select a new destination section');
      return;
    }
    setReshipSubmitting(true);
    try {
      const response = await apiClient.post(`${apiBase}/reship`, {
        transfer_id: reshipModal.transfer.transfer_id,
        new_destination_section: reshipSection,
        remarks: reshipRemarks || undefined,
      });
      toast.success(response.data?.message || `Transfer reshipped to ${reshipSection}`);
      handleCloseReshipModal();
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reship transfer'));
      setReshipSubmitting(false);
    }
  };

  const handleReset = () => {
    setStaffVerified(false);
    setStaffId('');
    setStaffName('');
    setScannedBundles([]);
    setTargetSection('');
    setRemarks('');
  };

  const outgoingCount = pendingShipments.length;
  const incomingCount = shippedTransfers.length;
  const rejectedCount = rejectedTransfers.length;

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-internal-transfer-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="outgoing" className="flex items-center gap-2" data-testid="outgoing-tab">
              <Send className="w-4 h-4" />
              Outgoing
              {outgoingCount > 0 && <Badge className="bg-orange-500 text-white ml-1">{outgoingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="incoming" className="flex items-center gap-2" data-testid="incoming-tab">
              <Truck className="w-4 h-4" />
              Incoming
              {incomingCount > 0 && <Badge className="bg-blue-500 text-white ml-1">{incomingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2" data-testid="rejected-tab">
              <RefreshCw className="w-4 h-4" />
              Rejected
              {rejectedCount > 0 && <Badge className="bg-red-500 text-white ml-1">{rejectedCount}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* OUTGOING */}
          <TabsContent value="outgoing">
            {pendingShipments.length > 0 && (
              <Card className="mb-6 border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Clock className="w-5 h-5" />
                    Pending Shipment Confirmations ({pendingShipments.length})
                  </CardTitle>
                  <CardDescription>Transfers requested by destination - confirm to ship</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingShipments.map((shipment) => (
                      <div
                        key={shipment.transfer_id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                              {shipment.transfer_id}
                            </Badge>
                            <span className="text-sm text-gray-600">→</span>
                            <Badge className="bg-purple-100 text-purple-700 border-0">{shipment.to_section}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {shipment.bundle_count || shipment.bundle_codes?.length || 0} bundles
                          </p>
                          <p className="text-xs text-gray-400">
                            Requested: {shipment.requested_at ? new Date(shipment.requested_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleConfirmShipment(shipment.transfer_id)}
                          disabled={confirmingShipment === shipment.transfer_id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-shipment-${shipment.transfer_id}`}
                        >
                          {confirmingShipment === shipment.transfer_id ? (
                            <>Confirming...</>
                          ) : (
                            <>
                              <Truck className="w-4 h-4 mr-2" />
                              Confirm Shipment
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-indigo-500" />
                  Create New Transfer
                </CardTitle>
                <CardDescription>Transfer bundles to another section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!staffVerified ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Staff ID</Label>
                      <div className="flex gap-2">
                        <Input
                          value={staffId}
                          onChange={(e) => setStaffId(e.target.value)}
                          placeholder="Enter staff ID"
                          onKeyDown={(e) => e.key === 'Enter' && handleStaffVerify()}
                          data-testid="staff-id-input"
                        />
                        <Button onClick={handleStaffVerify} data-testid="verify-staff-btn">
                          Verify
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium">{staffName}</span>
                        <span className="text-sm text-gray-500">({staffId})</span>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-staff-btn">
                        Change
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Destination Section</Label>
                      <Select value={targetSection} onValueChange={setTargetSection}>
                        <SelectTrigger data-testid="target-section-select">
                          <SelectValue placeholder="Select destination" />
                        </SelectTrigger>
                        <SelectContent>
                          {sections.map((section) => (
                            <SelectItem key={section.section_name} value={section.section_name}>
                              {section.section_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(targetSection || '').toLowerCase().includes('inhouse') && (
                      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                        <label className="flex items-center gap-2 font-medium text-amber-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isRework}
                            onChange={(e) => setIsRework(e.target.checked)}
                            data-testid="rework-toggle"
                            className="h-4 w-4 accent-amber-600"
                          />
                          Send for Rework / Re-Assortment
                        </label>
                        {isRework && (
                          <Input
                            value={reworkReason}
                            onChange={(e) => setReworkReason(e.target.value)}
                            placeholder="Reason for rework"
                            className="mt-2 bg-white"
                            data-testid="rework-reason-input"
                          />
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Scan Bundle Barcode</Label>
                      <div className="flex gap-2">
                        <Input
                          ref={barcodeRef}
                          value={bundleBarcode}
                          onChange={(e) => setBundleBarcode(e.target.value)}
                          placeholder="Scan or enter barcode"
                          onKeyDown={(e) => e.key === 'Enter' && handleScanBundle()}
                          data-testid="bundle-barcode-input"
                        />
                        <Button onClick={handleScanBundle} variant="outline" data-testid="scan-bundle-btn">
                          <Scan className="w-4 h-4 mr-2" />
                          Add
                        </Button>
                      </div>
                    </div>

                    {scannedBundles.length > 0 && (
                      <div className="space-y-2">
                        <Label>Scanned Bundles ({scannedBundles.length})</Label>
                        <div
                          className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto"
                          data-testid="scanned-bundles-list"
                        >
                          {scannedBundles.map((barcode) => (
                            <Badge key={barcode} variant="secondary" className="flex items-center gap-1">
                              {barcode}
                              <button
                                onClick={() => handleRemoveBundle(barcode)}
                                className="ml-1 hover:text-red-500"
                                data-testid={`remove-bundle-${barcode}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Remarks (Optional)</Label>
                      <Textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add any notes..."
                        rows={2}
                        data-testid="remarks-input"
                      />
                    </div>

                    <Button
                      onClick={handleSubmitTransfer}
                      disabled={!targetSection || scannedBundles.length === 0 || submitting}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="submit-transfer-btn"
                    >
                      {submitting ? 'Creating...' : `Create Transfer (${scannedBundles.length} bundles)`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INCOMING */}
          <TabsContent value="incoming">
            {shippedTransfers.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-gray-400">
                    <Truck className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p className="text-lg font-medium">No incoming shipments</p>
                    <p className="text-sm mt-1">Shipped transfers from other sections will appear here</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Truck className="w-5 h-5" />
                    Incoming Shipments ({shippedTransfers.length})
                  </CardTitle>
                  <CardDescription>Shipped transfers waiting for you to receive and accept</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {shippedTransfers.map((transfer) => (
                      <div
                        key={transfer.transfer_id}
                        className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                {transfer.transfer_id}
                              </Badge>
                              <span className="text-sm text-gray-600">from</span>
                              <Badge className="bg-indigo-100 text-indigo-700 border-0">
                                {transfer.from_section}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700 border-0">
                                <Truck className="w-3 h-3 mr-1" />
                                Shipped
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>{transfer.bundle_count || transfer.bundle_codes?.length || 0}</strong> bundles
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Shipped by {transfer.shipped_by_name || 'Unknown'} on{' '}
                              {transfer.shipped_at ? new Date(transfer.shipped_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleAcceptTransfer(transfer.transfer_id)}
                            disabled={acceptingTransfer === transfer.transfer_id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`accept-transfer-${transfer.transfer_id}`}
                          >
                            {acceptingTransfer === transfer.transfer_id ? (
                              <>Accepting...</>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Accept &amp; Receive
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* REJECTED */}
          <TabsContent value="rejected">
            {rejectedTransfers.length === 0 ? (
              <Card className="border-gray-200">
                <CardContent className="py-12 text-center text-gray-500">
                  <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No rejected transfers to reship</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <RefreshCw className="w-5 h-5" />
                    Rejected Transfers ({rejectedTransfers.length})
                  </CardTitle>
                  <CardDescription>
                    These transfers were rejected by the destination. You can reship them.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {rejectedTransfers.map((transfer) => (
                      <div
                        key={transfer.transfer_id}
                        className="p-4 bg-white rounded-lg border border-red-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                                {transfer.transfer_id}
                              </Badge>
                              <span className="text-sm text-gray-600">→</span>
                              <Badge className="bg-gray-100 text-gray-600 border-0 line-through">
                                {transfer.to_section}
                              </Badge>
                              <Badge className="bg-red-100 text-red-700 border-0">Rejected</Badge>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>{transfer.bundle_count || transfer.bundle_codes?.length || 0}</strong> bundles
                            </p>
                            <p className="text-sm text-red-600 mt-1">
                              <strong>Reason:</strong> {transfer.rejection_reason || 'No reason provided'}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleOpenReshipModal(transfer)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`reship-transfer-${transfer.transfer_id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reship
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Reship Modal */}
        {reshipModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  Reship Transfer
                </CardTitle>
                <CardDescription>
                  Select a new destination section for transfer {reshipModal.transfer?.transfer_id}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Bundles:</strong> {reshipModal.transfer?.bundle_codes?.length || 0}
                  </p>
                  <p className="text-sm text-red-600">
                    <strong>Previously rejected by:</strong> {reshipModal.transfer?.to_section}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>New Destination Section *</Label>
                  <Select value={reshipSection} onValueChange={setReshipSection}>
                    <SelectTrigger data-testid="reship-section-select">
                      <SelectValue placeholder="Select new section" />
                    </SelectTrigger>
                    <SelectContent>
                      {sections
                        .filter((s) => s.section_name !== reshipModal.transfer?.to_section)
                        .map((section) => (
                          <SelectItem key={section.section_name} value={section.section_name}>
                            {section.section_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Remarks (Optional)</Label>
                  <Textarea
                    value={reshipRemarks}
                    onChange={(e) => setReshipRemarks(e.target.value)}
                    placeholder="Add any notes..."
                    rows={2}
                    data-testid="reship-remarks-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleCloseReshipModal}
                    className="flex-1"
                    disabled={reshipSubmitting}
                    data-testid="cancel-reship-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReshipTransfer}
                    disabled={!reshipSection || reshipSubmitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-reship-btn"
                  >
                    {reshipSubmitting ? 'Reshipping...' : 'Confirm Reship'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        <div className="mt-6"><EwbDocPanel docType="branch_transfer" section={sectionName} title="Internal transfer e-way bills (goods leaving the site)" /></div>
      </main>
    </div>
  );
};

export default PerSectionInternalTransferClone;
